import { activeSiteCountry } from "@config/site/site-config";
import type { DiscountCode } from "@graphql/generated/types";
import { DiscountCodeState } from "@graphql/generated/types";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { useStore, useStoreApi } from "@store/index";
import { useEffect, useState } from "react";
import { getPromoCodeDiscountValue } from "../utils/getPromoCodeDiscountValue";
import { useGetHeaders } from "./useGetHeaders";
import {
    buildPromoCodesDiscountBreakdown,
    isPromoCodeMatchesCartWithNoDetectableEffect,
} from "../utils/buildPromoCodesDiscountBreakdown";
import { shouldShowInput } from "../utils/shouldShowInput";
import { removeCountryPrefix } from "../utils/removeCountryPrefix";
import {
    useApplyDiscountCodeToCart,
    useRemoveDiscountCodeFromCart,
} from "@lib/commerce/hooks/cartOperations";
import { useLocale } from "next-intl";

const discountCharsLimit = 30;

type Config = {
    setCartOrSessionChanged?: (cartOrSessionChanged: boolean) => void;
    cartOrSessionChanged?: boolean;
};

export const useGetConfig = ({ cartOrSessionChanged, setCartOrSessionChanged }: Config) => {
    const applyPromoCode = useApplyDiscountCodeToCart();
    const removePromoCode = useRemoveDiscountCodeFromCart();
    const locale = useLocale();
    const api = useStoreApi();

    const countryISOCode = activeSiteCountry;

    const [isApplying, setIsApplying] = useState(false);
    const [isRemoving, setIsRemoving] = useState<{ id: string; isRemoving: boolean }[]>([]);
    const [isPromoRemoving, setIsPromoRemoving] = useState(false);

    const { getLocalError, getDiscountCodeNoEffectMessage, headers } = useGetHeaders();

    const cart = useStore((state) => state.data.me?.cartInfo?.cart);
    const isLocked = useStore((state) => state.data.isLocked);

    const validPromoCodes =
        cart?.discountCodes?.filter(
            (discountCode) => discountCode?.state === DiscountCodeState.MatchesCart
        ) ?? [];

    const shouldShowCodeInput = shouldShowInput(countryISOCode, validPromoCodes.length);

    const getPromoCodeDiscountValueWithCart = (targetId: string) =>
        getPromoCodeDiscountValue(targetId, cart);

    const [addedPromocodesErrors, setAddedPromocodesErrors] = useState<
        { id?: string; message?: string; code?: string; reason?: "noEffect" | "warranty" }[]
    >([]);
    const [applyErrorMessage, setApplyErrorMessage] = useState<string>("");

    const setDraftPromoCode = (value: string) => api.getState().setDraftPromoCode(value);

    const enqueueNoEffectMessage = (discountCode: DiscountCode) => {
        const displayCode = removeCountryPrefix(discountCode.code ?? "", countryISOCode);
        const cms = getDiscountCodeNoEffectMessage(discountCode, countryISOCode)?.trim();
        const message =
            cms ||
            headers.discountCodeNoEffect?.trim() ||
            `The coupon ${displayCode} was not applied as it did not reduce your order total. Feel free to save it for your next purchase.`;

        setAddedPromocodesErrors((prev) => {
            if (prev.some((e) => e.id === discountCode.id && e.reason === "noEffect")) {
                return prev;
            }
            return [
                ...prev,
                { id: discountCode.id, code: displayCode, message, reason: "noEffect" as const },
            ];
        });
    };

    const onApply = async (promocode: string, clearValue: () => void) => {
        setApplyErrorMessage("");

        if (!cart?.id) {
            return;
        }

        setIsApplying(true);
        const fixedValue = promocode.toUpperCase();

        const { errors } = await applyPromoCode({
            cartId: cart.id,
            cartVersion: cart.version,
            storeKey: cart.store.key,
            locale: locale,
            discountCode: countryISOCode + fixedValue,
        });
        if (errors) {
            setIsApplying(false);
            setApplyErrorMessage(headers.error ?? "error");
            return;
        }

        clearValue();
        setIsApplying(false);
        // Only trigger cart change notification when promo code is successfully applied
        setCartOrSessionChanged?.(!cartOrSessionChanged);
    };

    const onRemove = async (discountId: string): Promise<boolean> => {
        setIsRemoving((prev) => [...prev, { id: discountId, isRemoving: true }]);

        const latestCart = api.getState().data.me?.cartInfo?.cart;
        if (!latestCart?.discountCodes?.some((d) => d?.discountCode?.id === discountId)) {
            setIsRemoving((prev) => prev.filter(({ id }) => id !== discountId));
            return false;
        }

        const { errors } = await removePromoCode({
            cartId: latestCart.id,
            cartVersion: latestCart.version,
            storeKey: latestCart.store.key,
            locale: locale,
            discountId,
            typeId: "discount-code",
        });

        if (errors) {
            Logger.error(ServiceType.WEB, "Error removing promo code", {
                tag: LogTag.DISCOUNTS,
                error: errors,
            });
            setIsRemoving((prev) => [...prev].filter(({ id }) => id !== discountId));
            return false;
        }

        setIsRemoving((prev) => [...prev].filter(({ id }) => id !== discountId));
        setCartOrSessionChanged?.(!cartOrSessionChanged);
        return true;
    };

    const defaultIndex = validPromoCodes.length ? [0] : undefined;

    const getIsRemoving = (discountCodeId: string) =>
        isRemoving.find(({ id }) => id === discountCodeId)?.isRemoving;

    const checkForErrors = async () => {
        setAddedPromocodesErrors((prev) => prev.filter((e) => e.reason === "noEffect"));

        const codesWithErrors = cart?.discountCodes?.filter((code) => {
            return code?.state !== DiscountCodeState.MatchesCart;
        });

        if (!codesWithErrors?.length) return;

        setIsPromoRemoving(true);

        for (const code of codesWithErrors) {
            const discountCode = code?.discountCode;
            const localError = getLocalError(discountCode, countryISOCode);

            setAddedPromocodesErrors((prev) => [
                ...prev,
                {
                    id: discountCode?.id,
                    code: removeCountryPrefix(discountCode?.code, countryISOCode),
                    message: localError || headers.error || "problem with voucher",
                    reason: "warranty" as const,
                },
            ]);

            await onRemove(discountCode?.id);
        }

        setIsPromoRemoving(false);
    };

    const removeMatchesCartWithNoDetectableEffect = async () => {
        const maxPasses = 20;
        for (let pass = 0; pass < maxPasses; pass++) {
            const latest = api.getState().data.me?.cartInfo?.cart;
            if (!latest) {
                break;
            }

            const breakdown = buildPromoCodesDiscountBreakdown(latest, countryISOCode);
            const target = breakdown.find(isPromoCodeMatchesCartWithNoDetectableEffect);
            if (!target) {
                break;
            }

            const discountCode = latest.discountCodes?.find(
                (c) => c?.discountCode?.id === target.discountCodeId
            )?.discountCode;
            if (!discountCode?.id) {
                break;
            }

            const removed = await onRemove(discountCode.id);
            if (removed) {
                enqueueNoEffectMessage(discountCode);
            }
        }
    };

    useEffect(() => {
        if (isRemoving.length) {
            return;
        }

        void (async () => {
            await checkForErrors();
            await removeMatchesCartWithNoDetectableEffect();
        })();
    }, [cart, cart?.version]);

    return {
        applyErrorMessage,
        applyPromoCode,
        checkForErrors,
        countryISOCode,
        defaultIndex,
        validPromoCodes,
        isLocked,
        shouldShowCodeInput,
        discountCharsLimit,
        addedPromocodesErrors,
        setDraftPromoCode,
        isPromoRemoving,
        headers,
        getIsRemoving,
        getPromoCodeDiscountValue: getPromoCodeDiscountValueWithCart,
        isApplying,
        isRemoving,
        onRemove,
        onApply,
        setAddedPromocodesErrors,
    };
};
